/**
 * Automatic GitLab (service account) and GitHub (App) onboarding.
 *
 * Calls the onboarding service directly until @ariksa/cloud-account is
 * regenerated with these endpoints. The manual token flow is unchanged.
 */
import { CloudAccountService } from 'api/services/services';

import { ONBOARDING_SERVICE } from '../service_urls';

const CONNECT_URL = `${ONBOARDING_SERVICE}/code-repository/connect`;

export type ConnectProvider = 'gitlab' | 'github';

export interface RepositoryConnectOptions {
  gitlab: boolean;
  github: boolean;
}

export interface RepositoryConnectResponse {
  uuid: string;
  name: string;
  cloud_type: string;
  status?: string;
}

interface GeneratedClient {
  axios: {
    get(url: string, config?: object): Promise<{ data: unknown }>;
    post(
      url: string,
      body: object,
      config?: object,
    ): Promise<{ data: unknown }>;
  };
  configuration?: { accessToken?: string | (() => string | Promise<string>) };
}

const client = (CloudAccountService.CodeRepository as unknown) as GeneratedClient;

async function authHeaders() {
  const token = client.configuration?.accessToken;
  const bearer = typeof token === 'function' ? await token() : token;
  return { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} };
}

export async function getRepositoryConnectOptions(): Promise<RepositoryConnectOptions> {
  const response = await client.axios.get(
    `${CONNECT_URL}/options`,
    await authHeaders(),
  );
  return response.data as RepositoryConnectOptions;
}

/** Returns the GitLab/GitHub URL to send the browser to. */
export async function startRepositoryConnect(
  provider: ConnectProvider,
  body: { name: string; group_path?: string },
): Promise<string> {
  const response = await client.axios.post(
    `${CONNECT_URL}/${provider}`,
    body,
    await authHeaders(),
  );
  return (response.data as { url: string }).url;
}

export async function completeRepositoryConnect(
  provider: ConnectProvider,
  body: { code: string; state: string; installation_id?: number },
): Promise<RepositoryConnectResponse> {
  const response = await client.axios.post(
    `${CONNECT_URL}/${provider}/complete`,
    body,
    await authHeaders(),
  );
  return response.data as RepositoryConnectResponse;
}

/** FastAPI puts the user-facing reason in `detail`. */
export function connectErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}
